import { CATALOG_VERSION, getCosmetic, type CosmeticKind } from '../content/cosmetics';
import { economyRandom, type IntegerRandom } from '../gacha/economyRandom';
import { phaseRewardRange, pullCost, rollClearReward, rollCosmetic, rollPhaseReward, validateCatalog, type PullCount } from '../gacha/gachaRules';

export type Progress = {
  schemaVersion: 1; revision: number; stones: number; ownedCounts: Record<string, number>;
  frameId: string; backgroundId: string;
};
export type PullResult = Readonly<{ itemId: string; isNew: boolean; ownedCount: number }>;
export type DrawReceipt = { id: string; kind: 'draw'; catalogVersion: number; count: PullCount; cost: number;
  pulls: readonly PullResult[]; createdAt: number; acknowledged: boolean };
export type ClearReceipt = { id: string; kind: 'clear'; amount: number; createdAt: number; acknowledged: boolean };
export type Receipt = DrawReceipt | ClearReceipt;
export const PROGRESS_DATABASE_VERSION = 2;
export type PhaseRewardLedger = {
  id: string; highestPhase: number; phaseAmounts: Record<number, number>; amount: number; updatedAt: number;
};
export type RunRewards = Readonly<{ runId: string; phase: PhaseRewardLedger | null; clearAmount: number; total: number }>;
export class ProgressError extends Error {
  constructor(public readonly code: 'unavailable' | 'invalid-data' | 'insufficient' | 'not-owned' | 'invalid-request', message: string) { super(message); }
}
export const initialProgress = (): Progress => ({ schemaVersion: 1, revision: 0, stones: 0,
  ownedCounts: { 'frame-default': 1, 'background-default': 1 }, frameId: 'frame-default', backgroundId: 'background-default' });
const safe = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
function validate(value: unknown): asserts value is Progress {
  const p = value as Progress | undefined;
  if (!p || p.schemaVersion !== 1 || !safe(p.revision) || !safe(p.stones) || !p.ownedCounts ||
    typeof p.ownedCounts !== 'object' || Array.isArray(p.ownedCounts) || Object.values(p.ownedCounts).some(n => !safe(n)) ||
    typeof p.frameId !== 'string' || typeof p.backgroundId !== 'string') {
    throw new ProgressError('invalid-data', '保存データを読み込めませんでした。データは変更していません。');
  }
}
function bump(p: Progress): void { p.revision += 1; validate(p); }
function validateReceipt(value: unknown): asserts value is Receipt {
  const r = value as Receipt | undefined;
  if (!r || typeof r.id !== 'string' || !r.id || !safe(r.createdAt) || typeof r.acknowledged !== 'boolean' ||
    (r.kind !== 'clear' && r.kind !== 'draw') ||
    (r.kind === 'clear' && (!safe(r.amount) || r.amount < 100 || r.amount > 9000)) ||
    (r.kind === 'draw' && (!safe(r.catalogVersion) || (r.count !== 1 && r.count !== 10) || r.cost !== r.count * 100 ||
      !Array.isArray(r.pulls) || r.pulls.length !== r.count || r.pulls.some(p => !p || typeof p.itemId !== 'string' ||
        typeof p.isNew !== 'boolean' || !safe(p.ownedCount) || p.ownedCount < 1)))) {
    throw new ProgressError('invalid-data', '獲得履歴を読み込めませんでした。保存データは変更していません。');
  }
}
function unavailable(): ProgressError { return new ProgressError('unavailable', '保存できませんでした。時間をおいて再試行してください。'); }
function validatePhaseLedger(value: unknown, runId: string): asserts value is PhaseRewardLedger {
  const r = value as PhaseRewardLedger | undefined;
  const invalid = () => new ProgressError('invalid-data', '共鳴の報酬履歴を読み込めませんでした。保存データは変更していません。');
  if (!r || r.id !== runId || !Number.isInteger(r.highestPhase) || r.highestPhase < 2 || r.highestPhase > 5 ||
    !safe(r.amount) || !safe(r.updatedAt) || !r.phaseAmounts || typeof r.phaseAmounts !== 'object' ||
    Array.isArray(r.phaseAmounts) || Object.keys(r.phaseAmounts).length !== r.highestPhase - 1) throw invalid();
  let total = 0;
  for (let phase = 2; phase <= r.highestPhase; phase++) {
    const amount = r.phaseAmounts[phase], range = phaseRewardRange(phase);
    if (!safe(amount) || amount < range.min || amount > range.max) throw invalid();
    total += amount;
  }
  if (total !== r.amount) throw invalid();
}

/** Every currency mutation reads the current profile inside the same write transaction as its receipt. */
export class ProgressStore {
  private opening: Promise<IDBDatabase> | null = null;
  constructor(public readonly databaseName = 'ishi-no-kyomei.progress', private readonly random: IntegerRandom = economyRandom) {}

  private open(): Promise<IDBDatabase> {
    if (this.opening) return this.opening;
    this.opening = new Promise<IDBDatabase>((resolve, reject) => {
      let request: IDBOpenDBRequest;
      try { request = indexedDB.open(this.databaseName, PROGRESS_DATABASE_VERSION); } catch { reject(unavailable()); return; }
      request.onupgradeneeded = event => {
        const db = request.result;
        if (event.oldVersion < 1) {
          db.createObjectStore('profile');
          db.createObjectStore('drawReceipts', { keyPath: 'id' });
          db.createObjectStore('clearReceipts', { keyPath: 'id' });
          request.transaction!.objectStore('profile').add(initialProgress(), 'main');
        }
        if (event.oldVersion < 2) db.createObjectStore('phaseRewards', { keyPath: 'id' });
      };
      request.onerror = () => reject(unavailable());
      request.onblocked = () => reject(new ProgressError('unavailable', '別のタブを閉じて、再試行してください。'));
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => { db.close(); this.opening = null; };
        resolve(db);
      };
    }).catch(error => { this.opening = null; throw error; });
    return this.opening;
  }

  async loadProgress(): Promise<Progress> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('profile', 'readonly');
      const request = tx.objectStore('profile').get('main');
      let profile: Progress;
      request.onsuccess = () => { try { validate(request.result); profile = request.result; } catch (e) { reject(e); } };
      tx.oncomplete = () => resolve(profile!);
      tx.onabort = tx.onerror = () => reject(unavailable());
    });
  }

  private async mutate<T>(storeName: 'clearReceipts' | 'drawReceipts' | null, id: string | null,
    change: (profile: Progress, previous: Receipt | undefined) => T): Promise<{ profile: Progress; result: T }> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName ? ['profile', storeName] : ['profile'], 'readwrite');
      const profileStore = tx.objectStore('profile');
      const profileRequest = profileStore.get('main');
      let output: { profile: Progress; result: T };
      let error: unknown;
      const apply = (previous?: Receipt) => {
        try {
          validate(profileRequest.result);
          if (previous !== undefined) validateReceipt(previous);
          const profile: Progress = profileRequest.result;
          const result = change(profile, previous);
          validate(profile);
          profileStore.put(profile, 'main');
          if (storeName) tx.objectStore(storeName).put(result);
          output = { profile, result };
        } catch (reason) { error = reason; tx.abort(); }
      };
      profileRequest.onsuccess = () => {
        if (storeName && id) {
          const existing = tx.objectStore(storeName).get(id);
          existing.onsuccess = () => apply(existing.result as Receipt | undefined);
        } else apply();
      };
      tx.oncomplete = () => resolve(output!);
      tx.onabort = () => reject(error ?? unavailable());
      tx.onerror = () => { /* onabort is the only failure settlement. */ };
    });
  }

  awardClear(runId: string): Promise<{ profile: Progress; result: ClearReceipt }> {
    if (!runId) return Promise.reject(new ProgressError('invalid-request', 'クリア情報が見つかりません。'));
    return this.mutate('clearReceipts', runId, (p, existing) => {
      if (existing) return existing as ClearReceipt;
      const amount = rollClearReward(this.random);
      p.stones += amount; bump(p);
      return { id: runId, kind: 'clear', amount, createdAt: Date.now(), acknowledged: false };
    });
  }

  async awardPhase(runId: string, phase: number): Promise<{ profile: Progress; ledger: PhaseRewardLedger | null; awardedAmount: number }> {
    if (!runId.trim()) throw new ProgressError('invalid-request', 'プレイ情報が見つかりません。');
    phaseRewardRange(phase);
    if (phase === 1) return { profile: await this.loadProgress(), ledger: null, awardedAmount: 0 };
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['profile', 'phaseRewards'], 'readwrite');
      const profiles = tx.objectStore('profile'), rewards = tx.objectStore('phaseRewards');
      const profileRequest = profiles.get('main'), rewardRequest = rewards.get(runId);
      let output: { profile: Progress; ledger: PhaseRewardLedger; awardedAmount: number };
      let error: unknown;
      // Requests in a transaction complete in order; both reads precede the write.
      rewardRequest.onsuccess = () => {
        try {
          validate(profileRequest.result);
          if (rewardRequest.result !== undefined) validatePhaseLedger(rewardRequest.result, runId);
          const profile: Progress = profileRequest.result;
          const ledger: PhaseRewardLedger = rewardRequest.result ?? { id: runId, highestPhase: 1, phaseAmounts: {}, amount: 0, updatedAt: 0 };
          let awardedAmount = 0;
          for (let next = ledger.highestPhase + 1; next <= phase; next++) {
            const amount = rollPhaseReward(next, this.random);
            ledger.phaseAmounts[next] = amount;
            ledger.amount += amount;
            ledger.highestPhase = next;
            awardedAmount += amount;
          }
          if (awardedAmount > 0) {
            ledger.updatedAt = Date.now();
            validatePhaseLedger(ledger, runId);
            profile.stones += awardedAmount; bump(profile);
            profiles.put(profile, 'main');
            rewards.put(ledger);
          }
          output = { profile, ledger, awardedAmount };
        } catch (reason) { error = reason; tx.abort(); }
      };
      tx.oncomplete = () => resolve(output!);
      tx.onabort = () => reject(error ?? unavailable());
      tx.onerror = () => { /* Settle only after abort, including both writes. */ };
    });
  }

  async loadRunRewards(runId: string): Promise<RunRewards> {
    if (!runId.trim()) throw new ProgressError('invalid-request', 'プレイ情報が見つかりません。');
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['phaseRewards', 'clearReceipts'], 'readonly');
      const phase = tx.objectStore('phaseRewards').get(runId), clear = tx.objectStore('clearReceipts').get(runId);
      tx.oncomplete = () => {
        try {
          if (phase.result !== undefined) validatePhaseLedger(phase.result, runId);
          if (clear.result !== undefined) {
            validateReceipt(clear.result);
            if (clear.result.kind !== 'clear' || clear.result.id !== runId) throw new ProgressError('invalid-data', 'クリア履歴が一致しません。');
          }
          const ledger: PhaseRewardLedger | null = phase.result ?? null;
          const clearAmount: number = clear.result?.amount ?? 0;
          resolve({ runId, phase: ledger, clearAmount, total: (ledger?.amount ?? 0) + clearAmount });
        } catch (error) { reject(error); }
      };
      tx.onabort = tx.onerror = () => reject(unavailable());
    });
  }

  draw(requestId: string, count: PullCount): Promise<{ profile: Progress; result: DrawReceipt }> {
    const cost = pullCost(count);
    validateCatalog();
    if (!requestId) return Promise.reject(new ProgressError('invalid-request', '抽選情報が見つかりません。'));
    return this.mutate('drawReceipts', requestId, (p, existing) => {
      if (existing) {
        const receipt = existing as DrawReceipt;
        if (receipt.count !== count) throw new ProgressError('invalid-request', '同じ抽選の回数は変更できません。');
        return receipt;
      }
      if (p.stones < cost) throw new ProgressError('insufficient', `観測石が${(cost - p.stones).toLocaleString('ja-JP')}個足りません。`);
      const pulls: PullResult[] = [];
      for (let i = 0; i < count; i++) {
        const item = rollCosmetic(this.random);
        const before = p.ownedCounts[item.id] ?? 0;
        p.ownedCounts[item.id] = before + 1;
        pulls.push({ itemId: item.id, isNew: before === 0, ownedCount: before + 1 });
      }
      p.stones -= cost; bump(p);
      return { id: requestId, kind: 'draw', catalogVersion: CATALOG_VERSION, count, cost, pulls, createdAt: Date.now(), acknowledged: false };
    });
  }

  async equip(kind: CosmeticKind, itemId: string): Promise<Progress> {
    return (await this.mutate(null, null, p => {
      const item = getCosmetic(itemId);
      if (!item || item.kind !== kind || !(p.ownedCounts[itemId]! > 0)) throw new ProgressError('not-owned', 'このスキンはまだ所持していません。');
      if (kind === 'board-frame') p.frameId = itemId; else p.backgroundId = itemId;
      bump(p);
    })).profile;
  }

  async pendingReceipts(): Promise<Receipt[]> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['clearReceipts', 'drawReceipts'], 'readonly');
      const clear = tx.objectStore('clearReceipts').getAll();
      const draw = tx.objectStore('drawReceipts').getAll();
      tx.oncomplete = () => {
        try {
          const receipts: unknown[] = [...clear.result, ...draw.result];
          receipts.forEach(validateReceipt);
          resolve((receipts as Receipt[]).filter(r => !r.acknowledged).sort((a, b) => a.createdAt - b.createdAt));
        } catch (error) { reject(error); }
      };
      tx.onabort = tx.onerror = () => reject(unavailable());
    });
  }

  async acknowledgeReceipt(receipt: Receipt): Promise<void> {
    await this.mutate(receipt.kind === 'clear' ? 'clearReceipts' : 'drawReceipts', receipt.id, (_p, previous) => {
      if (!previous) throw new ProgressError('invalid-data', '獲得結果が見つかりません。');
      return { ...previous, acknowledged: true };
    });
  }

  async close(): Promise<void> { const db = await this.opening; db?.close(); this.opening = null; }
}
